import React, { CSSProperties, FormEvent, useEffect, useRef, useState } from 'react';
import { PulseLoader } from 'react-spinners';
import styles from './BusinessInquiryForm.module.scss';
import APIService from '../../../lib/api.service';
import { Button } from '../../../components/Button/Button';
import checkDaylight from '../../../images/business-solutions-check-daylight.png';

const badges = [
  'Free site walk and solution review',
  'Clear next steps from our team',
  'Static IP requirement captured before follow-up',
];

const staticIpOptions = ['Yes', 'No', 'Not Sure'];

type FormData = {
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  businessAddress: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  phoneExt: string;
  currentProvider: string;
  staticIp: string;
  website: string; // honeypot
};

const initialFormData: FormData = {
  businessName: '',
  firstName: '',
  lastName: '',
  email: '',
  businessAddress: '',
  suite: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  phoneExt: '',
  currentProvider: '',
  staticIp: '',
  website: '',
};

export const BusinessInquiryForm = (): React.JSX.Element => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [showForm, setShowForm] = useState(true);
  const [showSuccessPrompt, setShowSuccessPrompt] = useState(false);
  const [showFailedPrompt, setShowFailedPrompt] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [staticIpOpen, setStaticIpOpen] = useState(false);
  const staticIpRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);

  const color = '#FFA500';
  const override: CSSProperties = {
    display: 'block',
    margin: '0 auto',
    borderColor: 'orange',
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (staticIpRef.current && !staticIpRef.current.contains(e.target as Node)) {
        setStaticIpOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showStatus = showLoader || showSuccessPrompt || showFailedPrompt;

  useEffect(() => {
    if (!showStatus) return;
    statusRef.current?.focus();
    statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showStatus, showLoader, showSuccessPrompt, showFailedPrompt]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    setShowLoader(true);
    setShowForm(false);
    setShowSuccessPrompt(false);
    setShowFailedPrompt(false);

    const addressParts = [
      formData.businessAddress,
      formData.suite || null,
      formData.city,
      formData.state,
      formData.zip,
    ].filter(Boolean).join(', ');

    const payload = {
      kind: 'business-solutions',
      businessName: formData.businessName,
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      phone: `${formData.phone} ${formData.phoneExt ? `ext. ${formData.phoneExt}` : ''}`.trim(),
      address: addressParts,
      currentProvider: formData.currentProvider,
      staticIp: formData.staticIp,
      website: formData.website, // honeypot
    };

    if (payload.website) {
      setShowSuccessPrompt(true);
      setShowLoader(false);
      return;
    }

    try {
      const submit = await APIService.postBusinessInterest(JSON.stringify(payload));

      setShowLoader(false);
      if (submit.status === 201) {
        setShowSuccessPrompt(true);
      } else {
        setShowFailedPrompt(true);
        setShowForm(true);
      }
    } catch (error) {
      setShowLoader(false);
      setShowFailedPrompt(true);
      setShowForm(true);
    }
  };

  return (
    <section id="business-inquiry-form" className={styles.section}>
      <div className="grid-12">
        <div className={styles.container}>
          <p className={styles.eyebrow}>Cityside Fiber Business Solutions</p>
          <h2 className={`${styles.headline} color--marina-blue`}>
            Bring fiber-optic internet to your business.
          </h2>
          <p className={styles.subtext}>
            Send us your business details and current connection.
            <br />
            We will confirm serviceability and follow up to coordinate your free site walk.
          </p>

          <ul className={styles.badges}>
            {badges.map((badge) => (
              <li key={badge}>
                <img src={checkDaylight} alt="" width="20" height="20" loading="lazy" />
                {badge}
              </li>
            ))}
          </ul>

          <div className={styles.card}>
            {showStatus && (
              <div
                ref={statusRef}
                className={styles.statusRegion}
                tabIndex={-1}
              >
                {showLoader && (
                  <div className={styles.loaderWrap}>
                    <PulseLoader
                      color={color}
                      loading={showLoader}
                      cssOverride={override}
                      size={50}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                    />
                  </div>
                )}

                {showSuccessPrompt && (
                  <h3 className="headline__h3 color--marina-blue">
                    Your interest has been submitted! Our Business Solutions team will reach out to
                    you as soon as possible. If you have any questions, please contact us at{' '}
                    <a href="mailto:concierge@citysidefiber.com">concierge@citysidefiber.com</a>.
                  </h3>
                )}

                {showFailedPrompt && (
                  <h3 className="headline__h3 color--marina-blue">
                    Hmm... it looks like we&apos;re having trouble submitting your inquiry. Try again
                    in a couple minutes, and if that doesn&apos;t work, please contact us at{' '}
                    <a href="mailto:concierge@citysidefiber.com">concierge@citysidefiber.com</a>.
                  </h3>
                )}
              </div>
            )}

            {showForm && (
              <form onSubmit={onSubmit}>
                <div className={styles.formInner}>
                <h3 className={styles.cardTitle}>
                  Tell us about your business.
                </h3>

                <div className={styles.honeypot} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                <div className="input-container">
                  <label htmlFor="businessName">Business Name</label>
                  <input
                    id="businessName"
                    name="businessName"
                    type="text"
                    placeholder="Business Name"
                    autoComplete="organization"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="multi-column-container">
                  <div className="input-container">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="First Name"
                      autoComplete="given-name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="input-container">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Last Name"
                      autoComplete="family-name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="input-container">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-container">
                  <label htmlFor="businessAddress">Business Address</label>
                  <input
                    id="businessAddress"
                    name="businessAddress"
                    type="text"
                    placeholder="Business Address"
                    autoComplete="address-line1"
                    value={formData.businessAddress}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="multi-column-container">
                  <div className="input-container">
                    <label htmlFor="suite">Suite / Unit</label>
                    <input
                      id="suite"
                      name="suite"
                      type="text"
                      placeholder="Suite / Unit"
                      autoComplete="address-line2"
                      value={formData.suite}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="input-container">
                    <label htmlFor="city">City</label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      placeholder="City"
                      autoComplete="address-level2"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="multi-column-container">
                  <div className="input-container">
                    <label htmlFor="state">State</label>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      placeholder="State"
                      autoComplete="address-level1"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="input-container">
                    <label htmlFor="zip">Zip Code</label>
                    <input
                      id="zip"
                      name="zip"
                      type="text"
                      inputMode="numeric"
                      placeholder="Zip Code"
                      autoComplete="postal-code"
                      pattern="^\d{5}(-\d{4})?$"
                      value={formData.zip}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="multi-column-container">
                  <div className="input-container">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      inputMode="tel"
                      placeholder="Phone Number"
                      pattern="[0-9]{3}-?[0-9]{3}-?[0-9]{4}"
                      title="Use format: 5551234567"
                      autoComplete="tel-national"
                      maxLength={12}
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="input-container">
                    <label htmlFor="businessPhoneExt">Extension</label>
                    <input
                      id="businessPhoneExt"
                      name="phoneExt"
                      inputMode="numeric"
                      placeholder="Extension"
                      maxLength={6}
                      pattern="^\d{1,6}$"
                      title="Digits only"
                      autoComplete="tel-extension"
                      value={formData.phoneExt}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="input-container">
                  <label htmlFor="currentProvider">Current Provider and Plan</label>
                  <input
                    id="currentProvider"
                    name="currentProvider"
                    type="text"
                    placeholder="Example: Cox Business—500 Mbps, or Not Sure"
                    value={formData.currentProvider}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-container">
                  <label htmlFor="staticIp">Static IP required?</label>
                  <div className={styles.customSelect} ref={staticIpRef}>
                    <button
                      type="button"
                      id="staticIp"
                      className={styles.customSelectTrigger}
                      onClick={() => setStaticIpOpen((open) => !open)}
                      aria-haspopup="listbox"
                      aria-expanded={staticIpOpen}
                    >
                      <span className={formData.staticIp ? '' : styles.customSelectPlaceholder}>
                        {formData.staticIp || 'Select One'}
                      </span>
                      <span className={`${styles.customSelectArrow} ${staticIpOpen ? styles.customSelectArrowOpen : ''}`} />
                    </button>

                    {staticIpOpen && (
                      <ul className={styles.customSelectDropdown} role="listbox">
                        {staticIpOptions.map((option) => (
                          <li
                            key={option}
                            className={`${styles.customSelectOption} ${formData.staticIp === option ? styles.customSelectOptionSelected : ''}`}
                            role="option"
                            aria-selected={formData.staticIp === option}
                            onClick={() => {
                              setFormData((prevData) => ({ ...prevData, staticIp: option }));
                              setStaticIpOpen(false);
                            }}
                          >
                            {option}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <p className={styles.disclaimer}>
                  By submitting, you agree that Cityside Fiber may contact you about your
                  availability request. We will never sell your information.
                </p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  fullWidth={true}
                  className={`${styles.submitBtn} bg-color--med-blue`}
                >
                  Submit Inquiry
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
